/**
 * Per-visitor tracking-consent signal (Accept All / Reject All), gating
 * Google Analytics, Contentsquare, and the chat widget from loading until a
 * visitor makes an explicit choice — required for EU/UK GDPR opt-in.
 *
 * Same module-level store-with-listeners shape as
 * `lib/floating-cta-visibility.ts`, extended with `localStorage` persistence
 * (an injected storage, following `lib/chat/nudges.ts`'s pattern), since a
 * visitor's choice must survive across sessions, unlike that file's
 * intentionally session-scoped storage.
 */

export type ConsentStatus = "accepted" | "rejected";

/**
 * "pending" — not yet read from storage; matches the server-rendered HTML,
 * so every consumer must render nothing consent-gated in this state or it
 * will hydration-mismatch.
 * "unset" — read from storage; no valid decision found. Show the banner.
 * "accepted" | "rejected" — a decision is on record. Never show the banner;
 * only "accepted" may load any of the three scripts.
 */
export type ConsentState = "pending" | "unset" | ConsentStatus;

export const CONSENT_STORAGE_KEY = "mutetaxes.consent";
/** Bump to force every visitor to be re-prompted after a policy change. */
export const CONSENT_VERSION = 1;

export interface ConsentRecord {
  status: ConsentStatus;
  version: number;
  timestamp: number;
}

/** The subset of the Storage interface this module needs. */
export interface ConsentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const isConsentStatus = (value: unknown): value is ConsentStatus =>
  value === "accepted" || value === "rejected";

/** A wrong/missing version or malformed JSON is treated as no decision. */
export const parseConsentRecord = (raw: string | null): ConsentRecord | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentRecord> | null;
    if (
      parsed &&
      isConsentStatus(parsed.status) &&
      parsed.version === CONSENT_VERSION &&
      typeof parsed.timestamp === "number"
    ) {
      return { status: parsed.status, version: parsed.version, timestamp: parsed.timestamp };
    }
    return null;
  } catch {
    return null;
  }
};

export const readConsentRecord = (
  storage: ConsentStorage | null | undefined,
): ConsentRecord | null => {
  if (!storage) return null;
  try {
    return parseConsentRecord(storage.getItem(CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
};

const writeConsentRecord = (
  storage: ConsentStorage | null | undefined,
  record: ConsentRecord,
): void => {
  if (!storage) return;
  try {
    storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage can be unavailable (blocked cookies, private browsing quotas).
    // In-memory state still holds for this page view; the banner will
    // reappear on the next load.
  }
};

type ConsentListener = (state: ConsentState) => void;
const listeners: ConsentListener[] = [];
let consentState: ConsentState = "pending";
let hasHydrated = false;

export const getConsentState = (): ConsentState => consentState;

const applyConsentState = (state: ConsentState): void => {
  if (consentState === state) return;
  consentState = state;
  listeners.forEach((listener) => listener(state));
};

/**
 * Replays the current value to a new subscriber immediately (mirrors
 * `ChatManager.subscribeToScript`'s replay-on-subscribe idiom in
 * lib/chat/chat.ts), so a consumer that subscribes after
 * `hydrateConsentState` already resolved still gets the real state instead
 * of being stuck at "pending".
 */
export const subscribeToConsentState = (listener: ConsentListener): (() => void) => {
  listeners.push(listener);
  listener(consentState);
  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) listeners.splice(index, 1);
  };
};

/**
 * Resolves "pending" to either a persisted decision or "unset". Safe to call
 * from every consent-aware component's mount effect — idempotent after the
 * first real call, so there is no "exactly one component must mount" coupling
 * to get wrong.
 */
export const hydrateConsentState = (storage: ConsentStorage | null | undefined): void => {
  if (hasHydrated) return;
  hasHydrated = true;
  const record = readConsentRecord(storage);
  applyConsentState(record ? record.status : "unset");
};

/**
 * Reopens the banner so a visitor can change an earlier decision — the
 * "Cookie Settings" control the privacy policy commits to providing.
 * Storage is untouched until `recordConsentDecision` is actually called, so
 * closing the reopened banner without deciding leaves the prior choice
 * intact on the next load.
 */
export const reopenConsentPrompt = (): void => {
  applyConsentState("unset");
};

/** Called by the banner's Accept All / Reject All buttons. */
export const recordConsentDecision = (
  storage: ConsentStorage | null | undefined,
  status: ConsentStatus,
  now: () => number = Date.now,
): void => {
  writeConsentRecord(storage, { status, version: CONSENT_VERSION, timestamp: now() });
  applyConsentState(status);
};

/**
 * Guards against storage access being blocked; only ever call from inside a
 * useEffect (post-mount, client-only) — never at module/render scope.
 */
export const readLocalStorage = (): ConsentStorage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** Test-only: clears the store, every subscriber, and the hydration latch. */
export const resetConsentState = (): void => {
  consentState = "pending";
  hasHydrated = false;
  listeners.length = 0;
};
